'use client';
import React, { useState } from 'react';
import { CheckCircle, QrCode, Hash, Globe, Calendar, Building2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';


type Certificate = {
  id: string;
  certId: string;
  company: string;
  product: string;
  category: string;
  certBody: string;
  country: string;
  flag: string;
  issueDate: string;
  expiryDate: string;
  status: string;
  trustScore: number;
  hash: string;
  recognizedMarkets: string[];
};

interface CertVerifyModalProps {
  cert: Certificate;
  onClose: () => void;
}

export default function CertVerifyModal({ cert, onClose }: CertVerifyModalProps) {
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const handleVerify = async () => {
    setVerifying(true);
    // Backend integration point: POST /api/certificates/verify with { certId, hash }
    await new Promise((r) => setTimeout(r, 1400));
    setVerifying(false);
    setVerified(true);
    toast.success('Certificate verified successfully');
  };

  const certBadgeVariant = (status: string): 'valid' | 'expiring' | 'review' | 'pending' | 'suspended' => {
    if (status === 'Valid') return 'valid';
    if (status === 'Expiring Soon') return 'expiring';
    if (status === 'Under Review') return 'review';
    return 'pending';
  };

  return (
    <Modal open onClose={onClose} title="Certificate Verification" size="md">
      <div className="space-y-5">
        {/* QR Visual */}
        <div
          className="flex flex-col items-center py-6 rounded-xl"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <div
            className="w-24 h-24 rounded-2xl flex items-center justify-center mb-3"
            style={{ background: 'var(--card)', border: '2px solid var(--border)' }}
          >
            <QrCode size={48} style={{ color: 'var(--emerald-bright)' }} />
          </div>
          <p className="text-xs font-mono-data font-semibold" style={{ color: 'var(--muted-foreground)' }}>
            {cert.certId}
          </p>
        </div>

        {/* Verification Status */}
        {verified ? (
          <div
            className="flex items-center gap-3 p-4 rounded-xl"
            style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.3)' }}
          >
            <CheckCircle size={22} style={{ color: 'var(--emerald-bright)' }} />
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--emerald-bright)' }}>✓ Verified Halal Certificate</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Certificate authenticity confirmed against HalalCham registry</p>
            </div>
          </div>
        ) : (
          <button
            onClick={handleVerify}
            disabled={verifying}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold disabled:opacity-70"
          >
            {verifying ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                Verifying Certificate...
              </>
            ) : (
              <>
                <QrCode size={16} />
                Verify Certificate
              </>
            )}
          </button>
        )}

        {/* Certificate Details */}
        <div className="space-y-3">
          {[
            { icon: Building2, label: 'Company', value: cert.company },
            { icon: ShieldCheck, label: 'Product', value: cert.product },
            { icon: Building2, label: 'Certification Body', value: cert.certBody },
            { icon: Calendar, label: 'Valid Until', value: cert.expiryDate },
            { icon: Globe, label: 'Country', value: `${cert.flag} ${cert.country}` },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={`verifydetail-${item.label}`}
                className="flex items-center justify-between px-4 py-2.5 rounded-lg"
                style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-center gap-2">
                  <Icon size={14} style={{ color: 'var(--muted-foreground)' }} />
                  <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
                </div>
                <span className="text-sm font-semibold">{item.value}</span>
              </div>
            );
          })}

          {/* Hash */}
          <div
            className="flex items-center justify-between px-4 py-2.5 rounded-lg"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <div className="flex items-center gap-2">
              <Hash size={14} style={{ color: 'var(--muted-foreground)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Verification Hash</span>
            </div>
            <span className="text-xs font-mono-data font-semibold" style={{ color: 'var(--emerald-bright)' }}>{cert.hash}</span>
          </div>

          {/* Status */}
          <div
            className="flex items-center justify-between px-4 py-2.5 rounded-lg"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
          >
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>Certificate Status</span>
            <StatusBadge variant={certBadgeVariant(cert.status)} label={cert.status} />
          </div>
        </div>

        {/* Recognized Markets */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}>
            Recognized Markets
          </p>
          <div className="flex flex-wrap gap-2">
            {cert.recognizedMarkets.map((market) => (
              <span
                key={`recmarket-${cert.id}-${market}`}
                className="text-xs px-2.5 py-1 rounded-full font-medium"
                style={{ background: 'rgba(10,107,79,0.12)', color: 'var(--emerald-bright)', border: '1px solid rgba(10,107,79,0.25)' }}
              >
                {market}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}